import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Shield, Users, ShoppingCart, CheckSquare, Package, FileText, BarChart3,
  Search, Edit, Trash2, Ban, UserCheck, Mail, Phone, Calendar,
  Loader2, AlertCircle, MessageSquare, Clock, ChevronRight, RefreshCw,
  UserCog, Banknote, Globe, Hash, Database, Table2, Plus, Download,
  Upload, Play, X, Copy, ChevronLeft, ChevronDown, Eye, FolderOpen,
  HardDrive, Filter, Code, Save, ArrowUpDown, MoreHorizontal, Minus,
  Crown, Sparkles, Zap, History, Send, CircleDot, CheckCircle2, XCircle,
  ArrowLeft, Lock, HelpCircle, Paperclip, Server, Activity, Cpu, MemoryStick,
  Bell, Megaphone, CreditCard, Wifi, WifiOff, ToggleLeft, ToggleRight, ExternalLink
} from 'lucide-react';
import { adminDbAPI, adminSubscriptionAPI, adminNewsAPI, adminTbankAPI } from '../../lib/api';

export default function AdminPage() {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <h2 className="text-xl font-bold mb-2">Доступ запрещён</h2>
        <p className="text-muted-foreground">Эта страница доступна только администраторам.</p>
      </div>
    );
  }

  return <AdminDashboard />;
}

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-950/40 flex items-center justify-center">
          <Shield className="h-5 w-5 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Админ-панель</h1>
          <p className="text-muted-foreground text-sm">Управление системой и пользователями</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7 max-w-4xl">
          <TabsTrigger value="overview" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Обзор</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-1.5">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Пользователи</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="gap-1.5">
            <Banknote className="h-4 w-4" />
            <span className="hidden sm:inline">Тарифы</span>
          </TabsTrigger>
          <TabsTrigger value="database" className="gap-1.5">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">База данных</span>
          </TabsTrigger>
          <TabsTrigger value="broadcast" className="gap-1.5">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Рассылка</span>
          </TabsTrigger>
          <TabsTrigger value="support" className="gap-1.5">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Поддержка</span>
          </TabsTrigger>
          <TabsTrigger value="server" className="gap-1.5">
            <Server className="h-4 w-4" />
            <span className="hidden sm:inline">Сервер</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
        <TabsContent value="pricing" className="mt-6">
          <PricingTab />
        </TabsContent>
        <TabsContent value="database" className="mt-6">
          <DatabaseTab />
        </TabsContent>
        <TabsContent value="broadcast" className="mt-6">
          <BroadcastTab />
        </TabsContent>
        <TabsContent value="support" className="mt-6">
          <SupportTab />
        </TabsContent>
        <TabsContent value="server" className="mt-6">
          <ServerTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== OVERVIEW TAB ====================

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки статистики');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;
  if (!stats) return null;

  const cards = [
    { label: 'Пользователей', value: stats.total_users, icon: Users, color: 'blue', sub: `+${stats.new_users_week} за неделю` },
    { label: 'Заблокировано', value: stats.blocked_users, icon: Ban, color: 'red' },
    { label: 'Заказов', value: stats.total_orders, icon: ShoppingCart, color: 'emerald' },
    { label: 'Задач', value: stats.total_tasks, icon: CheckSquare, color: 'purple' },
    { label: 'Клиентов', value: stats.total_customers, icon: Users, color: 'orange' },
    { label: 'Форм', value: stats.total_forms, icon: FileText, color: 'indigo' },
    { label: 'Материалов', value: stats.total_materials, icon: Package, color: 'teal' },
    { label: 'Выручка', value: formatMoney(stats.total_revenue), icon: Banknote, color: 'green', sub: `Предоплата: ${formatMoney(stats.total_prepayment)}` },
    { label: 'Тикеты', value: stats.total_tickets, icon: MessageSquare, color: 'yellow', sub: `Открыто: ${stats.open_tickets}` },
    { label: 'Подписки', value: stats.total_subscriptions || 0, icon: Crown, color: 'purple', sub: `Активных: ${stats.active_subscriptions || 0}` },
    { label: 'Доход подписок', value: formatMoney(stats.subscription_revenue || 0), icon: Sparkles, color: 'emerald' },
  ];

  const colorMap = {
    blue: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
    red: 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400',
    purple: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400',
    teal: 'bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400',
    green: 'bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-950/40 text-yellow-600 dark:text-yellow-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Статистика системы</h2>
        <Button variant="ghost" size="sm" onClick={loadStats}>
          <RefreshCw className="h-4 w-4 mr-1" /> Обновить
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[c.color]}`}>
                  <c.icon className="h-4.5 w-4.5" />
                </div>
              </div>
              <div className="text-2xl font-bold tracking-tight">{c.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.label}</div>
              {c.sub && <div className="text-[10px] text-muted-foreground/70 mt-1">{c.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Captcha Settings */}
      <CaptchaSettingsSection />

      {/* Inactive Users Section */}
      <InactiveUsersSection />
    </div>
  );
}

// ==================== CAPTCHA SETTINGS SECTION ====================

function CaptchaSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/captcha').then(res => {
      setEnabled(res.data.enabled);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleToggle = async (value) => {
    setSaving(true);
    try {
      await api.put('/admin/captcha', { enabled: value });
      setEnabled(value);
      toast.success(value ? 'Капча включена' : 'Капча выключена');
    } catch {
      toast.error('Ошибка сохранения настроек капчи');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-500" />
          Капча при регистрации
        </CardTitle>
        <CardDescription>Математическая капча для защиты от ботов</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">
              {enabled ? 'Включена' : 'Выключена'}
            </div>
            <div className="text-xs text-muted-foreground">
              При регистрации пользователь должен решить простой математический пример
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== INACTIVE USERS SECTION ====================

function InactiveUsersSection() {
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const loadInactive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/inactive-users?days=${days}`);
      setInactiveUsers(res.data.users);
    } catch (err) {
      toast.error('Ошибка загрузки неактивных пользователей');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadInactive();
  }, [loadInactive]);

  const handleDeleteUser = async (userId, userName) => {
    setDeletingId(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Пользователь ${userName} удалён`);
      setInactiveUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeletingId(null);
      setConfirmId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const getDaysLabel = (n) => {
    if (n === null || n === undefined) return 'Никогда не входил';
    if (n === 0) return 'Сегодня';
    if (n === 1) return '1 день назад';
    if (n < 5) return `${n} дня назад`;
    return `${n} дней назад`;
  };

  const getInactiveColor = (d) => {
    if (d === null) return 'text-red-500';
    if (d > 30) return 'text-red-500';
    if (d > 14) return 'text-orange-500';
    return 'text-yellow-600';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-500" />
          Неактивные пользователи
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Не заходили более</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 дней</SelectItem>
              <SelectItem value="7">7 дней</SelectItem>
              <SelectItem value="14">14 дней</SelectItem>
              <SelectItem value="30">30 дней</SelectItem>
              <SelectItem value="60">60 дней</SelectItem>
              <SelectItem value="90">90 дней</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : inactiveUsers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <UserCheck className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-60" />
            <p className="text-sm text-muted-foreground">Все пользователи активны!</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {inactiveUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${u.blocked ? 'bg-red-400' : u.role === 'admin' ? 'bg-red-600' : 'bg-gray-400'}`}>
                    {(u.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{u.name || 'Без имени'}</span>
                      {u.blocked && <Badge variant="outline" className="text-[10px] px-1 py-0 border-red-300 text-red-500">Blocked</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-semibold ${getInactiveColor(u.days_inactive)}`}>
                      {getDaysLabel(u.days_inactive)}
                    </p>
                    {u.last_login && (
                      <p className="text-[10px] text-muted-foreground">{formatDate(u.last_login)}</p>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-[10px] text-muted-foreground shrink-0">
                    <span className="flex items-center gap-0.5"><ShoppingCart className="h-3 w-3" />{u.orders_count}</span>
                  </div>
                  <div className="shrink-0">
                    {confirmId === u.id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 text-xs px-2"
                          disabled={deletingId === u.id}
                          onClick={() => handleDeleteUser(u.id, u.name || u.email)}
                        >
                          {deletingId === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Да'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => setConfirmId(null)}
                        >
                          Нет
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirmId(u.id)}
                        title="Удалить пользователя"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== USERS TAB ====================

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter && roleFilter !== 'all') params.set('role', roleFilter);
      const res = await api.get(`/admin/users?${params.toString()}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => {
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
  }, [loadUsers]);

  const openUserEdit = async (userId) => {
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setSelectedUser(res.data);
      setEditSheetOpen(true);
    } catch (err) {
      toast.error('Ошибка загрузки пользователя');
    }
  };

  const handleUserUpdated = () => {
    loadUsers();
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по имени, email, телефону..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Все роли" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все роли</SelectItem>
            <SelectItem value="master">Мастер</SelectItem>
            <SelectItem value="admin">Админ</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground">Всего: {total} пользователей</p>

      {loading ? (
        <LoadingState />
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Пользователи не найдены</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <Card
              key={u.id}
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => openUserEdit(u.id)}
            >
              <CardContent className="p-4 flex items-center gap-4">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${u.blocked ? 'bg-red-400' : u.role === 'admin' ? 'bg-red-600' : 'bg-blue-600'}`}>
                  {(u.name || 'U').charAt(0).toUpperCase()}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{u.name || 'Без имени'}</span>
                    {u.role === 'admin' && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Admin</Badge>}
                    {u.blocked && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-300 text-red-500">Заблокирован</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                {/* Stats */}
                <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><ShoppingCart className="h-3.5 w-3.5" />{u.orders_count}</span>
                  <span className="flex items-center gap-1"><CheckSquare className="h-3.5 w-3.5" />{u.tasks_count}</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{u.customers_count}</span>
                </div>
                {/* Last login */}
                <div className="hidden md:block text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('ru-RU', {day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit'}) : 'Нет входа'}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* User Edit Sheet */}
      {selectedUser && (
        <UserEditSheet
          user={selectedUser}
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
          onUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}

// ==================== USER EDIT SHEET ====================

function UserEditSheet({ user, open, onOpenChange, onUpdated }) {
  const { user: currentAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [form, setForm] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    role: user.role || 'master',
    currency: user.currency || 'RUB',
    blocked: user.blocked || false,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Orders & Tasks state
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const isSelf = user.id === currentAdmin?.id;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}`, form);
      toast.success('Пользователь обновлён');
      onUpdated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Пользователь удалён');
      onUpdated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Load orders when tab opens
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get(`/admin/users/${user.id}/orders`);
      setOrders(res.data.orders);
    } catch (err) {
      toast.error('Ошибка загрузки заказов');
    } finally {
      setOrdersLoading(false);
    }
  }, [user.id]);

  // Load tasks when tab opens
  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await api.get(`/admin/users/${user.id}/tasks`);
      setTasks(res.data.tasks);
    } catch (err) {
      toast.error('Ошибка загрузки задач');
    } finally {
      setTasksLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0) loadOrders();
    if (activeTab === 'tasks' && tasks.length === 0) loadTasks();
  }, [activeTab, loadOrders, loadTasks, orders.length, tasks.length]);

  const saveOrder = async (orderId, updates) => {
    try {
      await api.put(`/admin/users/${user.id}/orders/${orderId}`, updates);
      toast.success('Заказ обновлён');
      setEditingOrder(null);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const saveTask = async (taskId, updates) => {
    try {
      await api.put(`/admin/users/${user.id}/tasks/${taskId}`, updates);
      toast.success('Задача обновлена');
      setEditingTask(null);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const statusLabels = { new: 'Новый', in_progress: 'В работе', completed: 'Завершён', cancelled: 'Отменён' };
  const statusColors = { new: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300', in_progress: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300', completed: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300', cancelled: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' };
  const taskStatusLabels = { pending: 'Ожидает', in_progress: 'В работе', done: 'Готово' };
  const taskStatusColors = { pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300', in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300', done: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300' };
  const priorityLabels = { low: 'Низкий', medium: 'Средний', high: 'Высокий' };
  const priorityColors = { low: 'text-green-600', medium: 'text-yellow-600', high: 'text-red-600' };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="p-5 pb-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${user.role === 'admin' ? 'bg-red-600' : 'bg-blue-600'}`}>
              {(user.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <SheetTitle className="text-left">{user.name || 'Без имени'}</SheetTitle>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          {/* Tabs in header */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile" className="text-xs gap-1">
                <UserCog className="h-3.5 w-3.5" /> Профиль
              </TabsTrigger>
              <TabsTrigger value="subscription" className="text-xs gap-1">
                <Crown className="h-3.5 w-3.5" /> Подписка
              </TabsTrigger>
              <TabsTrigger value="orders" className="text-xs gap-1">
                <ShoppingCart className="h-3.5 w-3.5" /> Заказы <span className="text-[10px] opacity-60">({user.orders_count})</span>
              </TabsTrigger>
              <TabsTrigger value="tasks" className="text-xs gap-1">
                <CheckSquare className="h-3.5 w-3.5" /> Задачи <span className="text-[10px] opacity-60">({user.tasks_count})</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </SheetHeader>

        <ScrollArea className="flex-1">
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="p-5 space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-3">
                <StatMini icon={ShoppingCart} label="Заказов" value={user.orders_count} />
                <StatMini icon={CheckSquare} label="Задач" value={user.tasks_count} />
                <StatMini icon={Users} label="Клиентов" value={user.customers_count} />
                <StatMini icon={FileText} label="Форм" value={user.forms_count} />
                <StatMini icon={Package} label="Материалов" value={user.materials_count} />
                <StatMini icon={Banknote} label="Выручка" value={formatMoney(user.total_revenue)} />
              </div>

              <Separator />

              {/* Edit form */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <UserCog className="h-4 w-4" /> Данные пользователя
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Имя</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Телефон</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+7 999 123-45-67" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1"><Shield className="h-3 w-3" /> Роль</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isSelf}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="master">Мастер</SelectItem>
                        <SelectItem value="admin">Администратор</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1"><Banknote className="h-3 w-3" /> Валюта</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RUB">₽ Рубль</SelectItem>
                        <SelectItem value="USD">$ Доллар</SelectItem>
                        <SelectItem value="EUR">€ Евро</SelectItem>
                        <SelectItem value="KZT">₸ Тенге</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Block toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Заблокировать</p>
                  <p className="text-xs text-muted-foreground">Пользователь не сможет войти</p>
                </div>
                <Switch checked={form.blocked} onCheckedChange={(v) => setForm({ ...form, blocked: v })} disabled={isSelf} />
              </div>

              <Separator />

              {/* Meta info */}
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> Регистрация: {formatDate(user.created_at)}</div>
                <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Telegram: {user.telegram_chat_id ? `Chat ID ${user.telegram_chat_id}` : 'Не привязан'}</div>
                <div className="flex items-center gap-2"><Hash className="h-3.5 w-3.5" /> ID: {user.id}</div>
              </div>

              <Separator />

              {/* Delete section */}
              {!isSelf && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-destructive flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Опасная зона
                  </h3>
                  {!confirmDelete ? (
                    <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} className="w-full">
                      <Trash2 className="h-4 w-4 mr-2" /> Удалить пользователя и все данные
                    </Button>
                  ) : (
                    <div className="space-y-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <p className="text-sm text-destructive font-medium">Вы уверены? Это удалит все данные пользователя безвозвратно.</p>
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="flex-1">
                          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Да, удалить'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)} className="flex-1">
                          Отмена
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SUBSCRIPTION TAB */}
          {activeTab === 'subscription' && (
            <SubscriptionTab userId={user.id} userName={user.name} userEmail={user.email} />
          )}


          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="p-5 space-y-3">
              {ordersLoading ? (
                <LoadingState />
              ) : orders.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Заказов нет</p>
                </div>
              ) : (
                orders.map((order) => (
                  <Card key={order.id} className="overflow-hidden">
                    <CardContent className="p-3">
                      {editingOrder === order.id ? (
                        <OrderEditInline order={order} onSave={(u) => saveOrder(order.id, u)} onCancel={() => setEditingOrder(null)} />
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-sm font-semibold">{order.order_number_display || `№${order.order_number}`}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-700'}`}>
                                {statusLabels[order.status] || order.status}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{order.form_schema_name}</p>
                            {order.customer_name && <p className="text-xs text-muted-foreground">👤 {order.customer_name}</p>}
                            <div className="flex items-center gap-3 mt-1 text-xs">
                              <span className="font-medium">{formatMoney(order.total)}</span>
                              {order.prepayment > 0 && <span className="text-muted-foreground">предоплата: {formatMoney(order.prepayment)}</span>}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">{formatDate(order.created_at)}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setEditingOrder(order.id)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* TASKS TAB */}
          {activeTab === 'tasks' && (
            <div className="p-5 space-y-3">
              {tasksLoading ? (
                <LoadingState />
              ) : tasks.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <CheckSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Задач нет</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <Card key={task.id} className="overflow-hidden">
                    <CardContent className="p-3">
                      {editingTask === task.id ? (
                        <TaskEditInline task={task} onSave={(u) => saveTask(task.id, u)} onCancel={() => setEditingTask(null)} />
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-sm font-semibold truncate">{task.title}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${taskStatusColors[task.status] || 'bg-gray-100 text-gray-700'}`}>
                                {taskStatusLabels[task.status] || task.status}
                              </span>
                              <span className={`text-[10px] font-medium ${priorityColors[task.priority] || ''}`}>
                                {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'} {priorityLabels[task.priority]}
                              </span>
                            </div>
                            {task.description && <p className="text-xs text-muted-foreground line-clamp-1">{task.description}</p>}
                            <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                              {task.due_date && <span>📅 {formatDate(task.due_date)}</span>}
                              {task.order_number && <span>📦 Заказ {task.order_number_display || `№${task.order_number}`}</span>}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setEditingTask(task.id)}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </ScrollArea>

        {/* Footer - only show save on profile tab */}
        {activeTab === 'profile' && (
          <div className="p-4 border-t shrink-0 flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Отмена</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              Сохранить
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ==================== INLINE ORDER EDIT ====================

function OrderEditInline({ order, onSave, onCancel }) {
  const [status, setStatus] = useState(order.status);
  const [total, setTotal] = useState(order.total || 0);
  const [prepayment, setPrepayment] = useState(order.prepayment || 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ status, total: Number(total), prepayment: Number(prepayment) });
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold">Редактирование {order.order_number_display || `№${order.order_number}`}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Статус</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Новый</SelectItem>
              <SelectItem value="in_progress">В работе</SelectItem>
              <SelectItem value="completed">Завершён</SelectItem>
              <SelectItem value="cancelled">Отменён</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Сумма</Label>
          <Input type="number" value={total} onChange={(e) => setTotal(e.target.value)} className="h-8 text-xs" />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px]">Предоплата</Label>
        <Input type="number" value={prepayment} onChange={(e) => setPrepayment(e.target.value)} className="h-8 text-xs" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Сохранить'}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={onCancel}>Отмена</Button>
      </div>
    </div>
  );
}

// ==================== INLINE TASK EDIT ====================

function TaskEditInline({ task, onSave, onCancel }) {
  const [title, setTitle] = useState(task.title);
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [description, setDescription] = useState(task.description || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ title, status, priority, description });
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-[10px]">Название</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 text-xs" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">Статус</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Ожидает</SelectItem>
              <SelectItem value="in_progress">В работе</SelectItem>
              <SelectItem value="done">Готово</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Приоритет</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">🟢 Низкий</SelectItem>
              <SelectItem value="medium">🟡 Средний</SelectItem>
              <SelectItem value="high">🔴 Высокий</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px]">Описание</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} className="h-8 text-xs" placeholder="Описание задачи" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Сохранить'}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={onCancel}>Отмена</Button>
      </div>
    </div>
  );
}

// ==================== BROADCAST TAB ====================

function BroadcastTab() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState('all');
  const [inactiveDays, setInactiveDays] = useState('30');
  const [channels, setChannels] = useState({ email: true, telegram: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [confirmSend, setConfirmSend] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  // Load broadcast history
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/admin/broadcasts');
      setBroadcasts(res.data.broadcasts);
    } catch (err) {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  // Preview recipient count
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const params = new URLSearchParams();
        if (target === 'active' || target === 'inactive') {
          // Use inactive-users endpoint to get a rough count
          const res = await api.get(`/admin/stats`);
          if (target === 'all') {
            setPreviewCount(res.data.total_users - res.data.blocked_users);
          } else {
            setPreviewCount(null);
          }
        } else {
          const res = await api.get('/admin/stats');
          setPreviewCount(res.data.total_users - res.data.blocked_users);
        }
      } catch {
        setPreviewCount(null);
      }
    };
    loadPreview();
  }, [target]);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Заполните тему и текст рассылки');
      return;
    }
    const selectedChannels = [];
    if (channels.email) selectedChannels.push('email');
    if (channels.telegram) selectedChannels.push('telegram');
    if (selectedChannels.length === 0) {
      toast.error('Выберите хотя бы один канал отправки');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await api.post('/admin/broadcast', {
        subject: subject.trim(),
        message: message.trim(),
        target,
        inactive_days: Number(inactiveDays),
        channels: selectedChannels,
      });
      setResult(res.data);
      const parts = [];
      if (res.data.email_sent > 0) parts.push(`Email: ${res.data.email_sent}`);
      if (res.data.tg_sent > 0) parts.push(`Telegram: ${res.data.tg_sent}`);
      toast.success(`Рассылка отправлена! ${parts.join(', ')}`);
      setConfirmSend(false);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки рассылки');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const targetLabels = { all: 'Все пользователи', active: 'Активные', inactive: 'Неактивные' };

  return (
    <div className="space-y-6">
      {/* Compose */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4.5 w-4.5 text-blue-600" /> Новая рассылка
          </CardTitle>
          <CardDescription>Отправка email-уведомления пользователям сервиса</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Target audience */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Кому отправить</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: '👥 Все', desc: 'Все незаблокированные' },
                { value: 'active', label: '🟢 Активные', desc: 'Заходили недавно' },
                { value: 'inactive', label: '🔴 Неактивные', desc: 'Давно не заходили' },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTarget(t.value)}
                  className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-lg border text-left transition-colors ${
                    target === t.value
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <span className="text-sm font-medium block">{t.label}</span>
                  <span className="text-[10px] text-muted-foreground">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Inactive days threshold */}
          {(target === 'active' || target === 'inactive') && (
            <div className="space-y-1.5">
              <Label className="text-xs">Порог неактивности (дней)</Label>
              <Select value={inactiveDays} onValueChange={setInactiveDays}>
                <SelectTrigger className="w-[180px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 дней</SelectItem>
                  <SelectItem value="14">14 дней</SelectItem>
                  <SelectItem value="30">30 дней</SelectItem>
                  <SelectItem value="60">60 дней</SelectItem>
                  <SelectItem value="90">90 дней</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Channel selection */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Каналы отправки</Label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setChannels(c => ({ ...c, email: !c.email }))}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                  channels.email
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-500/20'
                    : 'border-border hover:border-blue-300'
                }`}
              >
                <Mail className={`h-4 w-4 ${channels.email ? 'text-blue-600' : 'text-muted-foreground'}`} />
                <div className="text-left">
                  <span className="text-sm font-medium block">📧 Email</span>
                  <span className="text-[10px] text-muted-foreground">Всем по email</span>
                </div>
              </button>
              <button
                onClick={() => setChannels(c => ({ ...c, telegram: !c.telegram }))}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                  channels.telegram
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-950/30 ring-1 ring-sky-500/20'
                    : 'border-border hover:border-sky-300'
                }`}
              >
                <MessageSquare className={`h-4 w-4 ${channels.telegram ? 'text-sky-600' : 'text-muted-foreground'}`} />
                <div className="text-left">
                  <span className="text-sm font-medium block">💬 Telegram</span>
                  <span className="text-[10px] text-muted-foreground">Кто привязал бота</span>
                </div>
              </button>
            </div>
          </div>

          <Separator />

          {/* Subject */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Тема письма</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Например: Обновление сервиса YaDela.ru"
              maxLength={200}
            />
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Текст рассылки</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Здравствуйте!\n\nМы рады сообщить вам о новых возможностях...\n\nС уважением,\nКоманда YaDela.ru"}
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-[120px]"
            />
            <p className="text-[10px] text-muted-foreground">Используйте пустые строки для разделения абзацев</p>
          </div>

          {/* Send */}
          {!confirmSend ? (
            <Button
              onClick={() => setConfirmSend(true)}
              disabled={!subject.trim() || !message.trim() || (!channels.email && !channels.telegram)}
              className="w-full sm:w-auto"
            >
              <Mail className="h-4 w-4 mr-2" />
              Отправить рассылку
            </Button>
          ) : (
            <div className="p-3 rounded-lg border border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30 space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-700 dark:text-orange-400">Подтвердите отправку</p>
                  <p className="text-xs text-orange-600/80 dark:text-orange-400/70 mt-1">
                    Аудитория: <strong>{targetLabels[target]}</strong>
                    {previewCount !== null && ` (~${previewCount} чел.)`}
                  </p>
                  <p className="text-xs text-orange-600/80 dark:text-orange-400/70">
                    Каналы: <strong>{[channels.email && '📧 Email', channels.telegram && '💬 Telegram'].filter(Boolean).join(' + ')}</strong>
                  </p>
                  <p className="text-xs text-orange-600/80 dark:text-orange-400/70">Тема: <strong>{subject}</strong></p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSend} disabled={sending} size="sm" className="flex-1">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                  {sending ? 'Отправка...' : 'Да, отправить'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmSend(false)} className="flex-1">
                  Отмена
                </Button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-800 space-y-2">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">✅ Рассылка завершена</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {(result.email_sent > 0 || result.email_failed > 0) && (
                  <div className="bg-white/60 dark:bg-white/5 rounded-md p-2">
                    <p className="font-medium">📧 Email</p>
                    <p className="text-green-600">Отправлено: {result.email_sent}</p>
                    {result.email_failed > 0 && <p className="text-red-500">Ошибки: {result.email_failed}</p>}
                  </div>
                )}
                {(result.tg_sent > 0 || result.tg_failed > 0 || result.tg_skipped > 0) && (
                  <div className="bg-white/60 dark:bg-white/5 rounded-md p-2">
                    <p className="font-medium">💬 Telegram</p>
                    <p className="text-green-600">Отправлено: {result.tg_sent}</p>
                    {result.tg_failed > 0 && <p className="text-red-500">Ошибки: {result.tg_failed}</p>}
                    {result.tg_skipped > 0 && <p className="text-muted-foreground">Без бота: {result.tg_skipped}</p>}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">Получателей: {result.recipients_total} · ID: #{result.broadcast_id}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" /> История рассылок
          </h3>
          {broadcasts.length > 0 && (
            <ClearButton
              label="Очистить историю"
              onConfirm={async () => {
                await api.delete('/admin/broadcasts');
                toast.success('История рассылок очищена');
                loadHistory();
              }}
            />
          )}
        </div>

        {historyLoading ? (
          <LoadingState />
        ) : broadcasts.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <Mail className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Рассылок пока не было</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {broadcasts.map((b) => (
              <Card key={b.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs text-muted-foreground">#{b.id}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {targetLabels[b.target] || b.target}
                        </Badge>
                        {b.channels && b.channels.map(ch => (
                          <span key={ch} className="text-[10px] text-muted-foreground">
                            {ch === 'email' ? '📧' : '💬'}
                          </span>
                        ))}
                        <span className="text-[10px] text-muted-foreground">{formatDate(b.created_at)}</span>
                      </div>
                      <h4 className="text-sm font-semibold truncate">{b.subject}</h4>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.message}</p>
                    </div>
                    <div className="text-right shrink-0 space-y-0.5">
                      {(b.email_sent > 0 || b.sent > 0) && (
                        <p className="text-xs">📧 <span className="font-bold text-green-600">{b.email_sent ?? b.sent}</span></p>
                      )}
                      {b.tg_sent > 0 && (
                        <p className="text-xs">💬 <span className="font-bold text-sky-600">{b.tg_sent}</span></p>
                      )}
                      <p className="text-[10px] text-muted-foreground">из {b.recipients_total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* News Management */}
      <NewsManagementSection />
    </div>
  );
}

// ==================== NEWS MANAGEMENT SECTION ====================

const EMOJI_OPTIONS = ['📢', '🎉', '🚀', '✨', '🔥', '💡', '🛠', '📦', '🎯', '⚡', '🌟', '📝', '🔔', '💰', '🎨', '🔧'];

function NewsManagementSection() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', emoji: '📢', published: true });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadNews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminNewsAPI.getAll();
      setNews(res.data.news || []);
    } catch {
      toast.error('Ошибка загрузки новостей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  const resetForm = () => {
    setForm({ title: '', content: '', emoji: '📢', published: true });
    setEditingItem(null);
    setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({ title: item.title, content: item.content || '', emoji: item.emoji || '📢', published: item.published !== false });
    setEditingItem(item);
    setShowForm(true);
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Заголовок обязателен');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await adminNewsAPI.update(editingItem.id, form);
        toast.success('Новость обновлена');
      } else {
        await adminNewsAPI.create(form);
        toast.success('Новость создана');
      }
      resetForm();
      loadNews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await adminNewsAPI.delete(id);
      toast.success('Новость удалена');
      setConfirmDeleteId(null);
      loadNews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeletingId(null);
    }
  };

  const togglePublished = async (item) => {
    try {
      await adminNewsAPI.update(item.id, { published: !item.published });
      toast.success(item.published ? 'Новость скрыта' : 'Новость опубликована');
      loadNews();
    } catch (err) {
      toast.error('Ошибка обновления');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Bell className="h-5 w-5 text-amber-500" /> Новости проекта
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadNews}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={startCreate}>
            <Plus className="h-4 w-4 mr-1" /> Новая
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Новости отображаются пользователям при нажатии на колокольчик в верхней панели
      </p>

      {/* Create/Edit form */}
      {showForm && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              {editingItem ? <Edit className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editingItem ? 'Редактировать новость' : 'Новая новость'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Emoji selector */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Иконка</Label>
              <div className="flex flex-wrap gap-1.5">
                {EMOJI_OPTIONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => setForm(f => ({ ...f, emoji: e }))}
                    className={`w-9 h-9 rounded-lg border text-lg flex items-center justify-center transition-all ${
                      form.emoji === e
                        ? 'border-primary bg-primary/10 ring-1 ring-primary/30 scale-110'
                        : 'border-border hover:border-primary/30 hover:bg-muted/50'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Заголовок *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Например: Новая функция — экспорт отчётов"
                maxLength={200}
              />
            </div>

            {/* Content */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Описание</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Подробное описание обновления..."
                rows={3}
                className="resize-y min-h-[70px]"
              />
            </div>

            {/* Published toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Опубликовать</p>
                <p className="text-xs text-muted-foreground">Видна пользователям в колокольчике</p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm(f => ({ ...f, published: v }))}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={saving || !form.title.trim()} className="flex-1 sm:flex-none">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingItem ? 'Сохранить' : 'Создать'}
              </Button>
              <Button variant="outline" onClick={resetForm} className="flex-1 sm:flex-none">
                Отмена
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* News list */}
      {loading ? (
        <LoadingState />
      ) : news.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Bell className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Новостей пока нет</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={startCreate}>
              <Plus className="h-4 w-4 mr-1" /> Создать первую новость
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {news.map((item) => (
            <Card key={item.id} className={`overflow-hidden transition-opacity ${!item.published ? 'opacity-60' : ''}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <span className="text-xl shrink-0 mt-0.5">{item.emoji || '📢'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-semibold">{item.title}</span>
                      {!item.published && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-500">
                          Скрыта
                        </Badge>
                      )}
                    </div>
                    {item.content && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.content}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{formatDate(item.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => togglePublished(item)}
                      title={item.published ? 'Скрыть' : 'Опубликовать'}
                    >
                      {item.published ? <Eye className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5 text-orange-500" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => startEdit(item)}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    {confirmDeleteId === item.id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                        >
                          {deletingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setConfirmDeleteId(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== SUPPORT TAB ====================

function SupportTab() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsEmail, setSettingsEmail] = useState('');
  const [settingsTelegram, setSettingsTelegram] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [tgLinked, setTgLinked] = useState(false);
  const [tgLinkCode, setTgLinkCode] = useState('');
  const [tgBotUsername, setTgBotUsername] = useState('');
  const [tgLinking, setTgLinking] = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await api.get('/admin/support-settings');
      setSettingsEmail(res.data.notification_email || '');
      setSettingsTelegram(res.data.telegram_username || '');
    } catch {}
    try {
      const tgRes = await api.get('/admin/support-settings/telegram/status');
      setTgLinked(tgRes.data.linked);
    } catch {}
    setSettingsLoading(false);
  }, []);

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await api.put('/admin/support-settings', {
        notification_email: settingsEmail,
        telegram_username: settingsTelegram
      });
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ошибка сохранения');
    }
    setSettingsSaving(false);
  };

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/admin/support-tickets?${params.toString()}`);
      setTickets(res.data.tickets);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Ошибка загрузки тикетов');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(loadTickets, 300);
    return () => clearTimeout(t);
  }, [loadTickets]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const TICKET_STATUS_MAP = {
    new: { label: 'Новый', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: CircleDot },
    in_progress: { label: 'В работе', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
    resolved: { label: 'Решён', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle2 },
    closed: { label: 'Закрыт', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', icon: XCircle },
  };

  return (
    <div className="space-y-4">
      {/* Settings panel */}
      <Card>
        <CardContent className="p-4">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <UserCog className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Настройки уведомлений</span>
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showSettings ? 'rotate-180' : ''}`} />
          </button>
          {showSettings && (
            <div className="mt-4 space-y-3 pt-3 border-t">
              {settingsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Загрузка...
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email для уведомлений
                      </label>
                      <Input
                        type="email"
                        value={settingsEmail}
                        onChange={(e) => setSettingsEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> Telegram (username)
                      </label>
                      <Input
                        value={settingsTelegram}
                        onChange={(e) => setSettingsTelegram(e.target.value)}
                        placeholder="username"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-muted-foreground">
                      Уведомления о новых тикетах и ответах будут приходить на указанный email.
                      Telegram отображается пользователям как резервный канал связи.
                    </p>
                    <Button size="sm" className="h-8 shrink-0 ml-3" onClick={saveSettings} disabled={settingsSaving}>
                      {settingsSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                      Сохранить
                    </Button>
                  </div>

                  {/* Telegram Bot Notifications */}
                  <div className="pt-3 border-t space-y-2">
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" /> Уведомления в Telegram (бот)
                    </label>
                    {tgLinked ? (
                      <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-sm text-emerald-700 dark:text-emerald-300">Telegram привязан — уведомления включены</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          onClick={async () => {
                            try {
                              await api.post('/admin/support-settings/telegram/unlink');
                              setTgLinked(false);
                              setTgLinkCode('');
                              toast.success('Telegram отвязан');
                            } catch { toast.error('Ошибка'); }
                          }}
                        >
                          Отвязать
                        </Button>
                      </div>
                    ) : tgLinkCode ? (
                      <div className="space-y-2 p-2.5 rounded-lg bg-muted/50 border border-border/50">
                        <p className="text-xs text-muted-foreground">
                          Нажмите кнопку ниже или отправьте боту команду <code className="text-primary font-mono">/start {tgLinkCode}</code>
                        </p>
                        <div className="flex items-center gap-2">
                          <a
                            href={`https://t.me/${tgBotUsername}?start=${tgLinkCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium transition-colors"
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            Открыть бота
                          </a>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={async () => {
                              const tgRes = await api.get('/admin/support-settings/telegram/status');
                              if (tgRes.data.linked) {
                                setTgLinked(true);
                                setTgLinkCode('');
                                toast.success('Telegram привязан!');
                              } else {
                                toast('Ожидаем привязку... Отправьте команду боту.');
                              }
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Проверить
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Код действует 10 минут</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted/50 border border-border/50">
                        <span className="text-xs text-muted-foreground">Привяжите Telegram для мгновенных уведомлений о тикетах</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs shrink-0"
                          disabled={tgLinking}
                          onClick={async () => {
                            setTgLinking(true);
                            try {
                              const res = await api.post('/admin/support-settings/telegram/generate-link');
                              setTgLinkCode(res.data.code);
                              setTgBotUsername(res.data.bot_username);
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Ошибка');
                            }
                            setTgLinking(false);
                          }}
                        >
                          {tgLinking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MessageSquare className="h-3 w-3 mr-1" />}
                          Привязать Telegram
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по теме, тексту, имени..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="new">Новые</SelectItem>
            <SelectItem value="in_progress">В работе</SelectItem>
            <SelectItem value="resolved">Решённые</SelectItem>
            <SelectItem value="closed">Закрытые</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Всего: {total} обращений</p>
        {tickets.length > 0 && (
          <div className="flex gap-2">
            {statusFilter === 'resolved' && (
              <ClearButton
                label="Удалить решённые"
                onConfirm={async () => {
                  await api.delete('/admin/support-tickets?status=resolved');
                  toast.success('Решённые тикеты удалены');
                  loadTickets();
                }}
              />
            )}
            <ClearButton
              label="Очистить все"
              onConfirm={async () => {
                const param = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
                await api.delete(`/admin/support-tickets${param}`);
                toast.success('Обращения удалены');
                loadTickets();
              }}
            />
          </div>
        )}
      </div>

      {loading ? (
        <LoadingState />
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Обращения не найдены</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const st = TICKET_STATUS_MAP[t.status] || TICKET_STATUS_MAP.new;
            const StIcon = st.icon;
            const repliesCount = (t.replies || []).length;
            const hasUserReply = (t.replies || []).some(r => r.author === 'user');
            return (
              <Card
                key={t.id}
                className="overflow-hidden cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm"
                onClick={() => setSelectedTicket(t)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-5 ${st.color} border-0`}>
                          <StIcon className="h-3 w-3 mr-0.5" />
                          {st.label}
                        </Badge>
                        <span className="text-xs font-mono text-muted-foreground">#{t.id}</span>
                        <span className="text-xs text-muted-foreground">{formatDate(t.updated_at || t.created_at)}</span>
                      </div>
                      <h4 className="text-sm font-semibold truncate">{t.subject}</h4>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{t.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {t.user_name}</span>
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {t.user_email}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <div className="flex items-center gap-2">
                        {repliesCount > 0 && (
                          <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                            <MessageSquare className="h-3 w-3" />
                            {repliesCount}
                          </span>
                        )}
                        {hasUserReply && (t.status === 'new' || t.status === 'in_progress') && (
                          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" title="Новый ответ пользователя" />
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Ticket Detail Sheet */}
      {selectedTicket && (
        <AdminTicketDetailSheet
          ticket={selectedTicket}
          onClose={() => { setSelectedTicket(null); loadTickets(); }}
          statusMap={TICKET_STATUS_MAP}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Admin Ticket Detail Sheet with reply functionality
function AdminTicketDetailSheet({ ticket, onClose, statusMap, formatDate }) {
  const [localTicket, setLocalTicket] = useState(ticket);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Load fresh ticket data (with replies)
  useEffect(() => {
    const fetchTicket = async () => {
      try {
        // Use admin list endpoint and find ticket by id
        const res = await api.get(`/admin/support-tickets?search=${ticket.id}`);
        const found = (res.data?.tickets || []).find(t => t.id === ticket.id);
        if (found) setLocalTicket(found);
      } catch {}
    };
    fetchTicket();
  }, [ticket.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localTicket?.replies]);

  const handleReply = async () => {
    const attachUrls = attachFiles.filter(f => !f.uploading).map(f => f.url);
    if (!replyText.trim() && !attachUrls.length) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/support-tickets/${localTicket.id}/reply`, {
        message: replyText.trim(),
        attachments: attachUrls
      });
      setLocalTicket(prev => ({
        ...prev,
        replies: [...(prev.replies || []), res.data.reply],
        status: res.data.status || prev.status,
        updated_at: new Date().toISOString()
      }));
      setReplyText('');
      setAttachFiles([]);
      toast.success('Ответ отправлен пользователю');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ошибка отправки ответа');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (fileList) => {
    setUploading(true);
    for (const file of Array.from(fileList)) {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name} — не изображение`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} — больше 10 МБ`); continue; }
      const preview = URL.createObjectURL(file);
      setAttachFiles(prev => [...prev, { url: preview, name: file.name, uploading: true }]);
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await api.post('/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setAttachFiles(prev => prev.map(f => f.url === preview ? { url: res.data.file_path, name: file.name, uploading: false } : f));
      } catch {
        toast.error(`Ошибка загрузки ${file.name}`);
        setAttachFiles(prev => prev.filter(f => f.url !== preview));
      }
    }
    setUploading(false);
  };

  const [previewUrl, setPreviewUrl] = React.useState(null);

  const renderAttachments = (attachments) => {
    if (!attachments || !attachments.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {attachments.map((url, i) => {
          const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;
          return (
            <button
              key={url + i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewUrl(fullUrl); }}
              className="block rounded-lg overflow-hidden border border-border/30 hover:border-primary/50 transition-colors cursor-zoom-in"
            >
              <img src={fullUrl} alt="" className="w-24 h-24 sm:w-32 sm:h-32 object-cover" loading="lazy" />
            </button>
          );
        })}
      </div>
    );
  };

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      await api.put(`/admin/support-tickets/${localTicket.id}`, { status: newStatus });
      setLocalTicket(prev => ({ ...prev, status: newStatus }));
      toast.success(`Статус изменён на «${(statusMap[newStatus] || {}).label || newStatus}»`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ошибка смены статуса');
    } finally {
      setChangingStatus(false);
    }
  };

  const formatFullDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const st = statusMap[localTicket.status] || statusMap.new;
  const StIcon = st.icon;
  const replies = localTicket.replies || [];
  const isClosed = localTicket.status === 'closed';

  return (
    <>
    <Sheet open onOpenChange={() => onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-4 sm:px-6 pt-5 pb-4 border-b shrink-0">
          <div className="flex items-start gap-3">
            <button onClick={onClose} className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted transition-colors mt-0.5">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-mono text-muted-foreground">#{localTicket.id}</span>
                <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-5 ${st.color} border-0`}>
                  <StIcon className="h-3 w-3 mr-0.5" />
                  {st.label}
                </Badge>
              </div>
              <SheetTitle className="text-base leading-tight text-left">{localTicket.subject}</SheetTitle>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {localTicket.user_name}</span>
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {localTicket.user_email}</span>
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(localTicket.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Status controls */}
          <div className="flex items-center gap-2 mt-3 ml-8">
            <span className="text-xs text-muted-foreground">Статус:</span>
            <Select value={localTicket.status} onValueChange={handleStatusChange} disabled={changingStatus}>
              <SelectTrigger className="h-7 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Новый</SelectItem>
                <SelectItem value="in_progress">В работе</SelectItem>
                <SelectItem value="resolved">Решён</SelectItem>
                <SelectItem value="closed">Закрыт</SelectItem>
              </SelectContent>
            </Select>
            {changingStatus && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
        </SheetHeader>

        {/* Messages thread */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {/* Original user message */}
            <div className="flex justify-start">
              <div className="max-w-[85%]">
                <p className="text-[11px] text-muted-foreground mb-1 ml-1 flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <Users className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                  </span>
                  {localTicket.user_name}
                </p>
                <div className="bg-muted rounded-2xl rounded-tl-md px-4 py-3">
                  <p className="text-sm whitespace-pre-wrap break-words">{localTicket.message}</p>
                  {renderAttachments(localTicket.attachments)}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 ml-1">
                  {formatFullDate(localTicket.created_at)}
                </p>
              </div>
            </div>

            {/* Replies */}
            {replies.map((reply) => {
              const isAdmin = reply.author === 'admin';
              return (
                <div key={reply.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[85%]">
                    {!isAdmin && (
                      <p className="text-[11px] text-muted-foreground mb-1 ml-1 flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                          <Users className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
                        </span>
                        {reply.author_name || localTicket.user_name}
                      </p>
                    )}
                    {isAdmin && (
                      <p className="text-[11px] text-muted-foreground mb-1 mr-1 text-right flex items-center justify-end gap-1">
                        {reply.author_name || 'Поддержка'}
                        <span className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                          <Shield className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" />
                        </span>
                      </p>
                    )}
                    <div className={
                      isAdmin
                        ? 'bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-3'
                        : 'bg-muted rounded-2xl rounded-tl-md px-4 py-3'
                    }>
                      <p className="text-sm whitespace-pre-wrap break-words">{reply.message}</p>
                      {renderAttachments(reply.attachments)}
                    </div>
                    <p className={`text-[11px] text-muted-foreground mt-1 ${isAdmin ? 'text-right mr-1' : 'ml-1'}`}>
                      {formatFullDate(reply.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Closed banner */}
            {isClosed && (
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
                  <Lock className="h-3 w-3" />
                  Тикет закрыт
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Reply input */}
        <div className="border-t px-4 sm:px-6 py-3 shrink-0 space-y-2">
          {attachFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachFiles.map((f, i) => (
                <div key={f.url + i} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-border/50">
                  <img src={f.url} alt={f.name} className="w-full h-full object-cover" />
                  {f.uploading && (
                    <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                  <button
                    onClick={() => setAttachFiles(prev => prev.filter(x => x.url !== f.url))}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Textarea
            placeholder={isClosed ? 'Тикет закрыт. Смените статус, чтобы ответить.' : 'Напишите ответ пользователю...'}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={3}
            className="resize-none text-sm"
            maxLength={5000}
            disabled={isClosed}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleReply();
              }
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files.length) { handleFileUpload(e.target.files); e.target.value = ''; } }}
          />
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground">
              Ответ будет отправлен на {localTicket.user_email}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => fileInputRef.current?.click()}
                disabled={isClosed || uploading}
                title="Прикрепить фото"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </Button>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">Ctrl+Enter</span>
              <Button size="sm" onClick={handleReply} disabled={sending || (!replyText.trim() && !attachFiles.filter(f => !f.uploading).length) || isClosed} className="h-8">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Ответить
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-2 sm:p-4 flex items-center justify-center bg-background/95 backdrop-blur-sm">
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

// ==================== HELPERS ====================


// ==================== DATABASE TAB ====================


function PricingTab() {
  const [pricing, setPricing] = useState(null);
  const [editPricing, setEditPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const PERIOD_LABELS = { '1': '1 месяц', '3': '3 месяца', '6': '6 месяцев', '12': '12 месяцев' };

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/pricing');
      setPricing(res.data);
      setEditPricing(JSON.parse(JSON.stringify(res.data.periods)));
    } catch (err) {
      toast.error('Ошибка загрузки тарифов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  const handlePriceChange = (period, value) => {
    const numVal = parseFloat(value) || 0;
    setEditPricing(prev => {
      const updated = { ...prev };
      updated[period] = {
        ...updated[period],
        price_per_month: numVal,
        total: Math.round(numVal * parseInt(period) * 100) / 100,
      };
      // Recalculate discounts
      const basePrice = updated['1']?.price_per_month || 0;
      for (const k of Object.keys(updated)) {
        if (k === '1') {
          updated[k].discount_percent = 0;
        } else if (basePrice > 0) {
          updated[k].discount_percent = Math.round((1 - updated[k].price_per_month / basePrice) * 100);
          if (updated[k].discount_percent < 0) updated[k].discount_percent = 0;
        }
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/pricing', { periods: editPricing });
      toast.success('Тарифы обновлены');
      loadPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(pricing?.periods) !== JSON.stringify(editPricing);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-orange-500" />
            Управление тарифами «Про»
          </CardTitle>
          <CardDescription>
            Настройте стоимость подписки для каждого периода. Скидки и итоговые суммы рассчитываются автоматически от цены за 1 месяц.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Header */}
            <div className="grid grid-cols-12 gap-3 text-xs font-medium text-muted-foreground px-1">
              <div className="col-span-3">Период</div>
              <div className="col-span-3">Цена / мес. (₽)</div>
              <div className="col-span-2 text-center">Скидка</div>
              <div className="col-span-2 text-right">Итого (₽)</div>
              <div className="col-span-2 text-right">Было</div>
            </div>
            
            <Separator />

            {['1', '3', '6', '12'].map(period => {
              const current = pricing?.periods?.[period];
              const edited = editPricing?.[period];
              const changed = current?.price_per_month !== edited?.price_per_month;
              
              return (
                <div key={period} className={`grid grid-cols-12 gap-3 items-center p-3 rounded-lg transition-colors ${changed ? 'bg-orange-50 dark:bg-orange-950/20 ring-1 ring-orange-200 dark:ring-orange-800' : 'bg-muted/30'}`}>
                  <div className="col-span-3">
                    <span className="font-medium text-sm">{PERIOD_LABELS[period]}</span>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="1"
                      step="10"
                      value={edited?.price_per_month || ''}
                      onChange={(e) => handlePriceChange(period, e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="col-span-2 text-center">
                    {edited?.discount_percent > 0 ? (
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        -{edited.discount_percent}%
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="font-semibold text-sm">{edited?.total?.toLocaleString('ru-RU')} ₽</span>
                  </div>
                  <div className="col-span-2 text-right">
                    {changed ? (
                      <span className="text-xs text-muted-foreground line-through">{current?.price_per_month} ₽/мес</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {hasChanges ? (
                <span className="text-orange-600 dark:text-orange-400 font-medium">● Есть несохранённые изменения</span>
              ) : (
                <span>Все изменения сохранены</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                disabled={!hasChanges}
                onClick={() => setEditPricing(JSON.parse(JSON.stringify(pricing?.periods)))}
              >
                Отменить
              </Button>
              <Button 
                size="sm"
                disabled={!hasChanges || saving}
                onClick={handleSave}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Сохранить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demo limits editor */}
      <DemoLimitsEditor />

      {/* Tbank payment settings */}
      <TbankSettingsEditor />
    </div>
  );
}

function TbankSettingsEditor() {
  const defaultSettings = {
    terminal_key: '', password: '', test_mode: true, enabled: false,
    fiscalization_enabled: false,
    taxation: 'usn_income', tax: 'none', ffd_version: '1.05',
    payment_method: 'full_payment', payment_object: 'service',
    company_email: '', company_inn: '',
    notification_url: '', success_url: '', fail_url: '',
    payment_description: 'Подписка YaDela Про — {period}',
    language: 'ru',
  };

  const [settings, setSettings] = useState(null);
  const [editSettings, setEditSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [payments, setPayments] = useState([]);
  const [showPayments, setShowPayments] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('main'); // main, fiscal, urls

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminTbankAPI.get();
      setSettings(res.data);
      setEditSettings({ ...defaultSettings, ...res.data });
    } catch (err) {
      toast.error('Ошибка загрузки настроек Т-Банка');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const upd = (field, value) => setEditSettings(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!editSettings.terminal_key.trim()) { toast.error('Укажите Terminal Key'); return; }
    if (!editSettings.password.trim()) { toast.error('Укажите Password'); return; }
    if (editSettings.fiscalization_enabled && !editSettings.company_email && !editSettings.company_inn) {
      toast.error('Для фискализации укажите Email или ИНН компании');
      return;
    }
    setSaving(true);
    try {
      await adminTbankAPI.update(editSettings);
      toast.success('Настройки Т-Банка сохранены');
      setTestResult(null);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await adminTbankAPI.update(editSettings);
      const res = await adminTbankAPI.test();
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Ошибка подключения' });
    } finally {
      setTesting(false);
    }
  };

  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await adminTbankAPI.getPayments();
      setPayments(res.data.payments || []);
    } catch (err) {
      toast.error('Ошибка загрузки платежей');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const togglePayments = () => {
    if (!showPayments) loadPayments();
    setShowPayments(!showPayments);
  };

  const statusColors = {
    confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    authorized: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    expired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  };
  const statusLabels = {
    confirmed: 'Оплачен', authorized: 'Авторизован', pending: 'Ожидание',
    rejected: 'Отклонён', expired: 'Истёк',
  };

  const taxationOptions = [
    { value: 'osn', label: 'ОСН — Общая' },
    { value: 'usn_income', label: 'УСН — Доходы' },
    { value: 'usn_income_outcome', label: 'УСН — Доходы минус расходы' },
    { value: 'esn', label: 'ЕСХН' },
    { value: 'patent', label: 'Патентная' },
  ];
  const taxOptions = [
    { value: 'none', label: 'Без НДС' },
    { value: 'vat0', label: 'НДС 0%' },
    { value: 'vat5', label: 'НДС 5%' },
    { value: 'vat7', label: 'НДС 7%' },
    { value: 'vat10', label: 'НДС 10%' },
    { value: 'vat20', label: 'НДС 20%' },
    { value: 'vat22', label: 'НДС 22% (с 01.01.2026)' },
    { value: 'vat105', label: 'НДС 5/105' },
    { value: 'vat107', label: 'НДС 7/107' },
    { value: 'vat110', label: 'НДС 10/110' },
    { value: 'vat120', label: 'НДС 20/120' },
    { value: 'vat122', label: 'НДС 22/122 (с 01.01.2026)' },
  ];
  const paymentMethodOptions = [
    { value: 'full_payment', label: 'Полный расчёт' },
    { value: 'full_prepayment', label: 'Предоплата 100%' },
    { value: 'prepayment', label: 'Предоплата' },
    { value: 'advance', label: 'Аванс' },
    { value: 'partial_payment', label: 'Частичный расчёт и кредит' },
    { value: 'credit', label: 'Передача в кредит' },
    { value: 'credit_payment', label: 'Оплата кредита' },
  ];
  const paymentObjectOptions = [
    { value: 'commodity', label: 'Товар' },
    { value: 'excise', label: 'Подакцизный товар' },
    { value: 'job', label: 'Работа' },
    { value: 'service', label: 'Услуга' },
    { value: 'gambling_bet', label: 'Ставка азартной игры' },
    { value: 'gambling_prize', label: 'Выигрыш азартной игры' },
    { value: 'lottery', label: 'Лотерейный билет' },
    { value: 'lottery_prize', label: 'Выигрыш лотереи' },
    { value: 'intellectual_activity', label: 'Интеллектуальная деятельность' },
    { value: 'payment', label: 'Платёж' },
    { value: 'agent_commission', label: 'Агентское вознаграждение' },
    { value: 'composite', label: 'Составной предмет расчёта' },
    { value: 'contribution', label: 'Выплата (ФФД 1.2)' },
    { value: 'property_rights', label: 'Имущественное право (ФФД 1.2)' },
    { value: 'unrealization', label: 'Внереализационный доход (ФФД 1.2)' },
    { value: 'tax_reduction', label: 'Иные платежи и взносы (ФФД 1.2)' },
    { value: 'trade_fee', label: 'Торговый сбор (ФФД 1.2)' },
    { value: 'resort_tax', label: 'Курортный сбор (ФФД 1.2)' },
    { value: 'pledge', label: 'Залог (ФФД 1.2)' },
    { value: 'income_decrease', label: 'Расход (ФФД 1.2)' },
    { value: 'another', label: 'Иной предмет расчёта' },
  ];

  if (loading) return (
    <Card>
      <CardContent className="p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="space-y-3"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
      </CardContent>
    </Card>
  );

  const hasChanges = settings && JSON.stringify({ ...defaultSettings, ...settings }) !== JSON.stringify(editSettings);

  const SectionButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveSection(id)}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeSection === id ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700' : 'text-muted-foreground hover:bg-muted/50 border border-transparent'}`}
    >
      {icon} {label}
    </button>
  );

  const SelectField = ({ label, value, options, onChange, hint }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full h-9 text-sm">
          <SelectValue placeholder="Выберите..." />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-yellow-500" />
            Платёжная система Т-Банк
          </CardTitle>
          <Badge variant={editSettings.enabled ? 'default' : 'secondary'} className={editSettings.enabled ? 'bg-emerald-500 text-white' : ''}>
            {editSettings.enabled ? 'Включена' : 'Выключена'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Интернет-эквайринг Т-Банк для приёма оплат за подписку. Получите реквизиты в{' '}
          <a href="https://business.tbank.ru" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-0.5">
            Т-Бизнес <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Section tabs */}
        <div className="flex gap-2 flex-wrap">
          <SectionButton id="main" label="Терминал" icon="🔑" />
          <SectionButton id="fiscal" label="Фискализация" icon="🧾" />
          <SectionButton id="urls" label="URL и описание" icon="🔗" />
        </div>

        {/* ========== MAIN SECTION ========== */}
        {activeSection === 'main' && (
          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="flex items-center gap-2">
                {editSettings.enabled ? <Wifi className="h-4 w-4 text-emerald-500" /> : <WifiOff className="h-4 w-4 text-muted-foreground" />}
                <span className="text-sm font-medium">Приём платежей</span>
              </div>
              <Switch checked={editSettings.enabled} onCheckedChange={(v) => upd('enabled', v)} />
            </div>

            {/* Terminal Key */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Terminal Key</Label>
              <Input value={editSettings.terminal_key} onChange={(e) => upd('terminal_key', e.target.value)} placeholder="Ваш TerminalKey из Т-Бизнес" className="font-mono text-sm" />
              <p className="text-[10px] text-muted-foreground">Идентификатор терминала, выдаётся при подключении эквайринга в Т-Бизнес</p>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Password (пароль терминала)</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={editSettings.password} onChange={(e) => upd('password', e.target.value)} placeholder="Пароль из настроек терминала" className="font-mono text-sm pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                  <Eye className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground">Пароль для подписи запросов (Token), выдаётся вместе с Terminal Key</p>
            </div>

            {/* Test mode */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
              <div>
                <span className="text-sm font-medium flex items-center gap-2">
                  <Code className="h-4 w-4 text-blue-500" />
                  Тестовый режим
                </span>
                <span className="text-xs text-muted-foreground">Проверка без реальных списаний. Используйте тестовые реквизиты из Т-Бизнес</span>
              </div>
              <Switch checked={editSettings.test_mode} onCheckedChange={(v) => upd('test_mode', v)} />
            </div>

            {/* Language */}
            <SelectField
              label="Язык платёжной формы"
              value={editSettings.language}
              options={[{ value: 'ru', label: 'Русский' }, { value: 'en', label: 'English' }]}
              onChange={(v) => upd('language', v)}
              hint="Язык интерфейса страницы оплаты Т-Банка"
            />
          </div>
        )}

        {/* ========== FISCAL SECTION ========== */}
        {activeSection === 'fiscal' && (
          <div className="space-y-4">
            {/* Enable fiscalization */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50">
              <div>
                <span className="text-sm font-medium flex items-center gap-2">
                  🧾 Фискализация (54-ФЗ)
                </span>
                <span className="text-xs text-muted-foreground">Автоматическая отправка чеков через онлайн-кассу, подключённую в Т-Бизнес</span>
              </div>
              <Switch checked={editSettings.fiscalization_enabled} onCheckedChange={(v) => upd('fiscalization_enabled', v)} />
            </div>

            {editSettings.fiscalization_enabled && (
              <>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                  ⚠️ Для работы фискализации необходимо подключить онлайн-кассу в личном кабинете Т-Бизнес и привязать её к терминалу.
                </div>

                {/* FFD Version */}
                <SelectField
                  label="Версия ФФД"
                  value={editSettings.ffd_version}
                  options={[{ value: '1.05', label: 'ФФД 1.05' }, { value: '1.2', label: 'ФФД 1.2' }]}
                  onChange={(v) => upd('ffd_version', v)}
                  hint="Формат фискальных данных. ФФД 1.2 — актуальный стандарт с расширенными реквизитами"
                />

                {/* Taxation */}
                <SelectField
                  label="Система налогообложения"
                  value={editSettings.taxation}
                  options={taxationOptions}
                  onChange={(v) => upd('taxation', v)}
                  hint="Должна совпадать с системой в онлайн-кассе. НПД (самозанятые) не поддерживается — чеки формируются через «Мой налог»"
                />

                {/* Tax (VAT) */}
                <SelectField
                  label="Ставка НДС"
                  value={editSettings.tax}
                  options={taxOptions}
                  onChange={(v) => upd('tax', v)}
                  hint="Ставка НДС для позиций в чеке"
                />

                {/* Payment Method */}
                <SelectField
                  label="Признак способа расчёта"
                  value={editSettings.payment_method}
                  options={paymentMethodOptions}
                  onChange={(v) => upd('payment_method', v)}
                  hint="Для подписок обычно используется «Полный расчёт» или «Предоплата 100%»"
                />

                {/* Payment Object */}
                <SelectField
                  label="Признак предмета расчёта"
                  value={editSettings.payment_object}
                  options={paymentObjectOptions}
                  onChange={(v) => upd('payment_object', v)}
                  hint="Для подписок на CRM — «Услуга», для лицензий — «Интеллектуальная деятельность»"
                />

                {/* Company details */}
                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-3">Данные компании для чеков</p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Email компании</Label>
                      <Input value={editSettings.company_email} onChange={(e) => upd('company_email', e.target.value)} placeholder="info@company.ru" className="text-sm" />
                      <p className="text-[10px] text-muted-foreground">Email отправителя чека. Если не указан — используется email покупателя</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">ИНН компании</Label>
                      <Input value={editSettings.company_inn} onChange={(e) => upd('company_inn', e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="1234567890" className="font-mono text-sm" maxLength={12} />
                      <p className="text-[10px] text-muted-foreground">ИНН организации или ИП (10 или 12 цифр)</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== URLS SECTION ========== */}
        {activeSection === 'urls' && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-400">
              💡 Все URL формируются автоматически из текущего домена. Заполняйте только если нужен кастомный адрес (например, для проксирования через свой домен).
            </div>

            {/* Notification URL */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Notification URL (вебхук)</Label>
              <Input value={editSettings.notification_url} onChange={(e) => upd('notification_url', e.target.value)} placeholder="Автоматически: {домен}/api/payment/notification" className="text-sm font-mono" />
              <p className="text-[10px] text-muted-foreground">URL для уведомлений от Т-Банка о статусе платежа. Оставьте пустым для автоопределения</p>
            </div>

            {/* Success URL */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Success URL</Label>
              <Input value={editSettings.success_url} onChange={(e) => upd('success_url', e.target.value)} placeholder="Автоматически: {домен}/payment/success" className="text-sm font-mono" />
              <p className="text-[10px] text-muted-foreground">Страница после успешной оплаты. Параметр payment_id добавляется автоматически</p>
            </div>

            {/* Fail URL */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Fail URL</Label>
              <Input value={editSettings.fail_url} onChange={(e) => upd('fail_url', e.target.value)} placeholder="Автоматически: {домен}/payment/fail" className="text-sm font-mono" />
              <p className="text-[10px] text-muted-foreground">Страница при ошибке оплаты. Параметр payment_id добавляется автоматически</p>
            </div>

            {/* Payment description */}
            <div className="space-y-1.5 pt-2 border-t border-border">
              <Label className="text-xs font-medium text-muted-foreground">Шаблон описания платежа</Label>
              <Input value={editSettings.payment_description} onChange={(e) => upd('payment_description', e.target.value)} placeholder="Подписка YaDela Про — {period}" className="text-sm" />
              <p className="text-[10px] text-muted-foreground">Описание на странице оплаты. Подстановки: <code className="bg-muted px-1 rounded">{'{period}'}</code> — период, <code className="bg-muted px-1 rounded">{'{price_per_month}'}</code> — цена/мес., <code className="bg-muted px-1 rounded">{'{total}'}</code> — итого, <code className="bg-muted px-1 rounded">{'{discount}'}</code> — скидка %</p>
            </div>
          </div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`p-3 rounded-lg border text-sm ${testResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-800 dark:text-emerald-400' : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-950/20 dark:border-red-800 dark:text-red-400'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              <span className="font-medium">{testResult.message}</span>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {hasChanges ? (
              <span className="text-orange-600 dark:text-orange-400 font-medium">● Есть несохранённые изменения</span>
            ) : (
              <span>Все изменения сохранены</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={testing || !editSettings.terminal_key || !editSettings.password} onClick={handleTest}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Activity className="h-4 w-4 mr-1" />}
              Проверить
            </Button>
            <Button size="sm" disabled={!hasChanges || saving} onClick={handleSave} className="bg-yellow-500 hover:bg-yellow-600 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Сохранить
            </Button>
          </div>
        </div>

        {/* Payments history */}
        <div className="pt-2 border-t border-border">
          <button onClick={togglePayments} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full">
            <History className="h-4 w-4" />
            <span>История платежей</span>
            <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${showPayments ? 'rotate-180' : ''}`} />
          </button>

          {showPayments && (
            <div className="mt-3 space-y-2">
              {paymentsLoading ? (
                <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : payments.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Платежей пока нет</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1.5">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/50 text-xs">
                      <div className="space-y-0.5">
                        <div className="font-medium">{p.user_email || 'N/A'}</div>
                        <div className="text-muted-foreground">
                          {p.period_label || `${p.period_months} мес.`}
                          {p.price_per_month ? ` · ${p.price_per_month} ₽/мес.` : ''}
                          {p.discount_percent > 0 ? ` · −${p.discount_percent}%` : ''}
                          {' · '}
                          {new Date(p.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{p.amount_rub} ₽</span>
                        <Badge variant="secondary" className={`text-[10px] ${statusColors[p.status] || ''}`}>
                          {statusLabels[p.status] || p.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



const LIMIT_LABELS = {
  orders: { label: 'Заказы', icon: '📦' },
  tasks: { label: 'Задачи', icon: '✅' },
  customers: { label: 'Клиенты', icon: '👥' },
  materials: { label: 'Материалы', icon: '📋' },
};

function DemoLimitsEditor() {
  const [limits, setLimits] = useState(null);
  const [editLimits, setEditLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadLimits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/demo-limits');
      setLimits(res.data.limits);
      setEditLimits({ ...res.data.limits });
    } catch (err) {
      toast.error('Ошибка загрузки лимитов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLimits(); }, [loadLimits]);

  const handleSaveLimits = async () => {
    setSaving(true);
    try {
      await api.put('/admin/demo-limits', { limits: editLimits });
      toast.success('Лимиты демо обновлены');
      loadLimits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const hasLimitChanges = limits && editLimits && JSON.stringify(limits) !== JSON.stringify(editLimits);

  if (loading) return (
    <Card>
      <CardContent className="p-6">
        <Skeleton className="h-6 w-40 mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-500" />
            Лимиты «Демо»
          </CardTitle>
          {hasLimitChanges && (
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setEditLimits({ ...limits })}
              >
                Отменить
              </Button>
              <Button 
                size="sm"
                disabled={saving}
                onClick={handleSaveLimits}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Сохранить
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Максимальное количество записей для пользователей на бесплатном тарифе
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {editLimits && Object.entries(LIMIT_LABELS).map(([key, config]) => (
            <div key={key} className="flex flex-col items-center p-4 rounded-xl bg-muted/30 border border-border/50">
              <span className="text-2xl mb-2">{config.icon}</span>
              <span className="text-sm font-medium text-muted-foreground mb-2">{config.label}</span>
              <Input
                type="number"
                min="0"
                value={editLimits[key] ?? 0}
                onChange={(e) => setEditLimits(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                className="w-20 text-center h-9 font-bold text-lg"
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function DatabaseTab() {
  const [dbStats, setDbStats] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCol, setSelectedCol] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [fields, setFields] = useState([]);
  const [docTotal, setDocTotal] = useState(0);
  const [docPage, setDocPage] = useState(0);
  const [docLimit] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('_id');
  const [sortOrder, setSortOrder] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editJson, setEditJson] = useState('');
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [newDocJson, setNewDocJson] = useState('{\n  \n}');
  const [showQuery, setShowQuery] = useState(false);
  const [queryFilter, setQueryFilter] = useState('{}');
  const [queryResult, setQueryResult] = useState(null);
  const [queryCollection, setQueryCollection] = useState('');
  const [showNewCol, setShowNewCol] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'json'
  const [confirmDrop, setConfirmDrop] = useState(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [showFullDbImport, setShowFullDbImport] = useState(false);
  const [fullDbImportFile, setFullDbImportFile] = useState(null);
  const [fullDbClearExisting, setFullDbClearExisting] = useState(false);
  const [fullDbImporting, setFullDbImporting] = useState(false);
  const [fullDbExporting, setFullDbExporting] = useState(false);
  const [fullDbImportResult, setFullDbImportResult] = useState(null);
  const [confirmFullImport, setConfirmFullImport] = useState(false);
  const fullDbFileRef = React.useRef(null);

  const loadDbStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminDbAPI.getStats();
      setDbStats(res.data);
      setCollections(res.data.collections || []);
      if (!selectedCol && res.data.collections?.length > 0) {
        setSelectedCol(res.data.collections[0].name);
      }
    } catch (err) {
      toast.error('Ошибка загрузки статистики БД');
    } finally {
      setLoading(false);
    }
  }, [selectedCol]);

  const loadDocuments = useCallback(async () => {
    if (!selectedCol) return;
    try {
      setDocsLoading(true);
      const res = await adminDbAPI.getDocuments(selectedCol, {
        skip: docPage * docLimit,
        limit: docLimit,
        sort_field: sortField,
        sort_order: sortOrder,
        search: searchQuery || '',
      });
      setDocuments(res.data.documents || []);
      setFields(res.data.fields || []);
      setDocTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Ошибка загрузки документов');
    } finally {
      setDocsLoading(false);
    }
  }, [selectedCol, docPage, docLimit, sortField, sortOrder, searchQuery]);

  useEffect(() => { loadDbStats(); }, []);
  useEffect(() => { if (selectedCol) { setDocPage(0); } }, [selectedCol]);
  useEffect(() => { loadDocuments(); }, [selectedCol, docPage, sortField, sortOrder]);

  const handleSearch = () => { setDocPage(0); loadDocuments(); };
  const handleRefresh = () => { loadDbStats(); loadDocuments(); };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getDocId = (doc) => doc.id || doc._id || '';

  const handleSelectCol = (name) => {
    setSelectedCol(name);
    setSearchQuery('');
    setSortField('_id');
    setSortOrder(-1);
    setEditingDoc(null);
    setShowNewDoc(false);
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await adminDbAPI.deleteDocument(selectedCol, getDocId(doc));
      toast.success('Документ удалён');
      setConfirmDeleteDoc(null);
      loadDocuments();
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleSaveDoc = async () => {
    try {
      const parsed = JSON.parse(editJson);
      await adminDbAPI.updateDocument(selectedCol, getDocId(editingDoc), parsed);
      toast.success('Документ обновлён');
      setEditingDoc(null);
      loadDocuments();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Неверный JSON');
      } else {
        toast.error(err.response?.data?.detail || 'Ошибка сохранения');
      }
    }
  };

  const handleCreateDoc = async () => {
    try {
      const parsed = JSON.parse(newDocJson);
      await adminDbAPI.createDocument(selectedCol, parsed);
      toast.success('Документ создан');
      setShowNewDoc(false);
      setNewDocJson('{\n  \n}');
      loadDocuments();
      loadDbStats();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Неверный JSON');
      } else {
        toast.error(err.response?.data?.detail || 'Ошибка создания');
      }
    }
  };

  const handleCreateCollection = async () => {
    if (!newColName.trim()) return;
    try {
      await adminDbAPI.createCollection(newColName.trim());
      toast.success(`Коллекция "${newColName}" создана`);
      setShowNewCol(false);
      setNewColName('');
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания коллекции');
    }
  };

  const handleDropCollection = async (name) => {
    try {
      await adminDbAPI.dropCollection(name);
      toast.success(`Коллекция "${name}" удалена`);
      setConfirmDrop(null);
      if (selectedCol === name) {
        setSelectedCol(null);
        setDocuments([]);
      }
      loadDbStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления коллекции');
    }
  };

  const handleExport = async (colName) => {
    try {
      const res = await adminDbAPI.exportCollection(colName);
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${colName}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Экспорт завершён');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  const handleImport = async () => {
    try {
      const parsed = JSON.parse(importJson);
      const docs = Array.isArray(parsed) ? parsed : [parsed];
      await adminDbAPI.importCollection(selectedCol, docs);
      toast.success('Импорт завершён');
      setShowImport(false);
      setImportJson('');
      loadDocuments();
      loadDbStats();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Неверный JSON');
      } else {
        toast.error(err.response?.data?.detail || 'Ошибка импорта');
      }
    }
  };

  const handleRunQuery = async () => {
    if (!queryCollection) { toast.error('Выберите коллекцию'); return; }
    try {
      const filter = JSON.parse(queryFilter);
      const res = await adminDbAPI.runQuery({ collection: queryCollection, filter, limit: 100 });
      setQueryResult(res.data);
      toast.success(`Найдено: ${res.data.total} документов`);
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error('Неверный JSON фильтр');
      } else {
        toast.error(err.response?.data?.detail || 'Ошибка запроса');
      }
    }
  };

  const handleExportAll = async () => {
    try {
      setFullDbExporting(true);
      const res = await adminDbAPI.exportAll();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const ts = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `db_backup_${ts}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Экспорт всей базы данных завершён');
    } catch (err) {
      toast.error('Ошибка экспорта базы данных');
    } finally {
      setFullDbExporting(false);
    }
  };

  const handleImportAllConfirm = () => {
    if (!fullDbImportFile) { toast.error('Выберите файл'); return; }
    if (fullDbClearExisting) {
      setConfirmFullImport(true);
    } else {
      handleImportAll();
    }
  };

  const handleImportAll = async () => {
    if (!fullDbImportFile) return;
    try {
      setFullDbImporting(true);
      setConfirmFullImport(false);
      setFullDbImportResult(null);
      const res = await adminDbAPI.importAll(fullDbImportFile, fullDbClearExisting);
      setFullDbImportResult(res.data);
      toast.success(res.data.message || 'Импорт завершён');
      loadDbStats();
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка импорта базы данных');
    } finally {
      setFullDbImporting(false);
    }
  };

  const truncateValue = (val, max = 60) => {
    if (val === null || val === undefined) return <span className="text-muted-foreground italic">null</span>;
    if (typeof val === 'boolean') return <Badge variant={val ? 'default' : 'secondary'}>{val ? 'true' : 'false'}</Badge>;
    if (typeof val === 'object') {
      const str = JSON.stringify(val);
      return <span className="text-xs font-mono text-muted-foreground">{str.length > max ? str.slice(0, max) + '…' : str}</span>;
    }
    const str = String(val);
    return str.length > max ? str.slice(0, max) + '…' : str;
  };

  if (loading) return <LoadingState />;

  const totalPages = Math.ceil(docTotal / docLimit);
  const currentColInfo = collections.find(c => c.name === selectedCol);

  return (
    <div className="space-y-4">
      {/* DB Stats Header */}
      {dbStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-cyan-200 dark:border-cyan-900/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-950/50 flex items-center justify-center">
                <Database className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <div className="text-xl font-bold">{dbStats.collections_count}</div>
                <div className="text-xs text-muted-foreground">Коллекций</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-violet-200 dark:border-violet-900/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-950/50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <div className="text-xl font-bold">{dbStats.total_documents}</div>
                <div className="text-xs text-muted-foreground">Документов</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 dark:border-amber-900/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center">
                <HardDrive className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="text-xl font-bold">{formatBytes(dbStats.data_size)}</div>
                <div className="text-xs text-muted-foreground">Размер данных</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 dark:border-emerald-900/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center">
                <Table2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="text-xl font-bold">{formatBytes(dbStats.index_size)}</div>
                <div className="text-xs text-muted-foreground">Индексы</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Обновить
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowNewCol(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Коллекция
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setShowQuery(!showQuery); setQueryResult(null); }}>
          <Code className="h-3.5 w-3.5 mr-1.5" /> Запрос
        </Button>
        <Separator orientation="vertical" className="h-6 mx-1" />
        <Button size="sm" variant="outline" onClick={handleExportAll} disabled={fullDbExporting} className="border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30">
          {fullDbExporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          Экспорт БД
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setShowFullDbImport(true); setFullDbImportFile(null); setFullDbClearExisting(false); setFullDbImportResult(null); }} className="border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30">
          <Upload className="h-3.5 w-3.5 mr-1.5" /> Импорт БД
        </Button>
      </div>

      {/* Full DB Import Dialog */}
      {showFullDbImport && (
        <Card className="border-2 border-dashed border-blue-300 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center">
                <Upload className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Импорт всей базы данных
            </CardTitle>
            <CardDescription>Загрузите JSON-файл, полученный через «Экспорт БД». Все коллекции из файла будут импортированы.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs mb-2 block">JSON-файл бэкапа</Label>
              <div className="flex items-center gap-3">
                <input
                  ref={fullDbFileRef}
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={(e) => { setFullDbImportFile(e.target.files[0] || null); setFullDbImportResult(null); }}
                />
                <Button size="sm" variant="outline" className="h-9" onClick={() => fullDbFileRef.current?.click()}>
                  <FolderOpen className="h-3.5 w-3.5 mr-1.5" /> Выбрать файл
                </Button>
                {fullDbImportFile && (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="font-medium truncate max-w-[200px]">{fullDbImportFile.name}</span>
                    <span className="text-xs text-muted-foreground">({(fullDbImportFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50">
              <Switch
                checked={fullDbClearExisting}
                onCheckedChange={setFullDbClearExisting}
                id="clear-existing"
              />
              <div>
                <Label htmlFor="clear-existing" className="text-sm font-medium cursor-pointer">Очистить перед импортом</Label>
                <div className="text-xs text-muted-foreground mt-0.5">Удалить все документы в существующих коллекциях перед вставкой новых</div>
              </div>
            </div>
            {fullDbImportResult && (
              <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50">
                <div className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Импорт завершён
                </div>
                <div className="text-xs space-y-1">
                  <div>Коллекций обработано: <span className="font-medium">{fullDbImportResult.collections_processed}</span></div>
                  <div>Документов импортировано: <span className="font-medium">{fullDbImportResult.total_imported}</span></div>
                  {fullDbImportResult.details && (
                    <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5">
                      {fullDbImportResult.details.map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <Badge variant={d.status === 'ok' ? 'secondary' : 'destructive'} className="text-[10px] h-4 px-1">{d.status}</Badge>
                          <span className="font-mono">{d.collection}</span>
                          {d.imported !== undefined && <span className="text-muted-foreground">— {d.imported} док.</span>}
                          {d.reason && <span className="text-destructive">{d.reason}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={handleImportAllConfirm} disabled={!fullDbImportFile || fullDbImporting}>
                {fullDbImporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                {fullDbImporting ? 'Импорт...' : 'Импортировать'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowFullDbImport(false)}>Отмена</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirm dangerous full import */}
      {confirmFullImport && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmFullImport(false)}>
          <Card className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base text-destructive flex items-center gap-2"><AlertCircle className="h-5 w-5" /> Очистить и импортировать?</CardTitle>
              <CardDescription>Все существующие документы в коллекциях из файла будут <strong>удалены</strong> перед импортом. Это действие необратимо.</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setConfirmFullImport(false)}>Отмена</Button>
              <Button variant="destructive" size="sm" onClick={handleImportAll}>Очистить и импортировать</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New collection dialog */}
      {showNewCol && (
        <Card className="border-dashed border-2 border-cyan-300 dark:border-cyan-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Имя коллекции (latin)"
                value={newColName}
                onChange={(e) => setNewColName(e.target.value)}
                className="max-w-xs"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
              />
              <Button size="sm" onClick={handleCreateCollection}><Plus className="h-3.5 w-3.5 mr-1" />Создать</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowNewCol(false)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Query panel */}
      {showQuery && (
        <Card className="border-violet-200 dark:border-violet-900/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Code className="h-4 w-4" /> Произвольный запрос (find)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <Label className="text-xs mb-1 block">Коллекция</Label>
                <Select value={queryCollection} onValueChange={setQueryCollection}>
                  <SelectTrigger className="w-48 h-9 text-sm"><SelectValue placeholder="Выберите..." /></SelectTrigger>
                  <SelectContent>
                    {collections.map(c => <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={handleRunQuery} className="h-9"><Play className="h-3.5 w-3.5 mr-1" /> Выполнить</Button>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Фильтр (JSON)</Label>
              <textarea
                className="w-full h-20 rounded-md border bg-muted/30 p-3 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                value={queryFilter}
                onChange={(e) => setQueryFilter(e.target.value)}
                placeholder='{"email": {"$regex": "test"}}'
                spellCheck={false}
              />
            </div>
            {queryResult && (
              <div className="mt-2">
                <div className="text-xs text-muted-foreground mb-2">Найдено: {queryResult.total} | Показано: {queryResult.count}</div>
                <ScrollArea className="max-h-80 rounded-md border">
                  <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-all">
                    {JSON.stringify(queryResult.documents, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Layout: Collections sidebar + Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Collections sidebar */}
        <Card className="lg:max-h-[calc(100vh-320px)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Коллекции</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[400px] lg:max-h-[calc(100vh-400px)]">
              <div className="space-y-0.5 p-2">
                {collections.map(col => (
                  <div
                    key={col.name}
                    className={`group flex items-center justify-between rounded-md px-3 py-2 cursor-pointer transition-colors text-sm ${
                      selectedCol === col.name
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted/60'
                    }`}
                    onClick={() => handleSelectCol(col.name)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className={`h-3.5 w-3.5 flex-shrink-0 ${selectedCol === col.name ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="truncate">{col.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{col.count}</Badge>
                      <div className="hidden group-hover:flex items-center gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExport(col.name); }}
                          className="p-1 rounded hover:bg-muted"
                          title="Экспорт JSON"
                        >
                          <Download className="h-3 w-3 text-muted-foreground" />
                        </button>
                        {col.name !== 'users' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDrop(col.name); }}
                            className="p-1 rounded hover:bg-destructive/10"
                            title="Удалить коллекцию"
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Drop collection confirm */}
        {confirmDrop && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDrop(null)}>
            <Card className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-base text-destructive flex items-center gap-2"><AlertCircle className="h-5 w-5" /> Удалить коллекцию?</CardTitle>
                <CardDescription>Коллекция «{confirmDrop}» и все документы будут удалены безвозвратно.</CardDescription>
              </CardHeader>
              <CardContent className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setConfirmDrop(null)}>Отмена</Button>
                <Button variant="destructive" size="sm" onClick={() => handleDropCollection(confirmDrop)}>Удалить</Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Documents panel */}
        <Card>
          {selectedCol ? (
            <>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold">{selectedCol}</CardTitle>
                    <Badge variant="outline" className="text-[10px]">{docTotal} док.</Badge>
                    {currentColInfo && <Badge variant="secondary" className="text-[10px]">{formatBytes(currentColInfo.size)}</Badge>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant={viewMode === 'table' ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setViewMode('table')}><Table2 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={viewMode === 'json' ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setViewMode('json')}><Code className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {/* Search & actions */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      className="pl-8 h-8 text-sm"
                      placeholder='Поиск или JSON-фильтр {"field": "value"}'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <Button size="sm" variant="outline" className="h-8" onClick={handleSearch}><Filter className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowNewDoc(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Документ</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setShowImport(true)}><Upload className="h-3.5 w-3.5 mr-1" /> Импорт</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => handleExport(selectedCol)}><Download className="h-3.5 w-3.5 mr-1" /> Экспорт</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* New document form */}
                {showNewDoc && (
                  <div className="mx-4 mb-3 p-3 border rounded-lg border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-950/10">
                    <div className="text-xs font-medium mb-2 flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400"><Plus className="h-3.5 w-3.5" /> Новый документ (JSON)</div>
                    <textarea
                      className="w-full h-32 rounded-md border bg-background p-3 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newDocJson}
                      onChange={(e) => setNewDocJson(e.target.value)}
                      spellCheck={false}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-xs" onClick={handleCreateDoc}><Save className="h-3 w-3 mr-1" /> Создать</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowNewDoc(false)}>Отмена</Button>
                    </div>
                  </div>
                )}

                {/* Import form */}
                {showImport && (
                  <div className="mx-4 mb-3 p-3 border rounded-lg border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10">
                    <div className="text-xs font-medium mb-2 flex items-center gap-1.5 text-blue-700 dark:text-blue-400"><Upload className="h-3.5 w-3.5" /> Импорт JSON (массив или объект)</div>
                    <textarea
                      className="w-full h-32 rounded-md border bg-background p-3 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                      value={importJson}
                      onChange={(e) => setImportJson(e.target.value)}
                      placeholder='[{"name": "test"}, {"name": "test2"}]'
                      spellCheck={false}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-xs" onClick={handleImport}><Upload className="h-3 w-3 mr-1" /> Импортировать</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowImport(false)}>Отмена</Button>
                    </div>
                  </div>
                )}

                {/* Edit document */}
                {editingDoc && (
                  <div className="mx-4 mb-3 p-3 border rounded-lg border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/10">
                    <div className="text-xs font-medium mb-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-400"><Edit className="h-3.5 w-3.5" /> Редактирование: {getDocId(editingDoc)}</div>
                    <textarea
                      className="w-full h-48 rounded-md border bg-background p-3 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                      value={editJson}
                      onChange={(e) => setEditJson(e.target.value)}
                      spellCheck={false}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="h-7 text-xs" onClick={handleSaveDoc}><Save className="h-3 w-3 mr-1" /> Сохранить</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingDoc(null)}>Отмена</Button>
                    </div>
                  </div>
                )}

                {/* Documents list */}
                {docsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    Коллекция пуста
                  </div>
                ) : viewMode === 'json' ? (
                  /* JSON view */
                  <ScrollArea className="max-h-[500px]">
                    <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-all">
                      {JSON.stringify(documents, null, 2)}
                    </pre>
                  </ScrollArea>
                ) : (
                  /* Table view */
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground w-8">#</th>
                          {fields.slice(0, 8).map(f => (
                            <th
                              key={f}
                              className="px-3 py-2 text-left font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                              onClick={() => {
                                if (sortField === f) setSortOrder(prev => prev * -1);
                                else { setSortField(f); setSortOrder(-1); }
                              }}
                            >
                              <div className="flex items-center gap-1">
                                <span className="truncate max-w-[120px]">{f}</span>
                                {sortField === f && <ArrowUpDown className="h-3 w-3 flex-shrink-0" />}
                              </div>
                            </th>
                          ))}
                          {fields.length > 8 && <th className="px-3 py-2 text-left font-medium text-muted-foreground">...</th>}
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground w-20">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc, idx) => (
                          <tr key={getDocId(doc) || idx} className="border-b hover:bg-muted/20 transition-colors">
                            <td className="px-3 py-2 text-muted-foreground">{docPage * docLimit + idx + 1}</td>
                            {fields.slice(0, 8).map(f => (
                              <td key={f} className="px-3 py-2 max-w-[200px] truncate">
                                {truncateValue(doc[f], 50)}
                              </td>
                            ))}
                            {fields.length > 8 && <td className="px-3 py-2 text-muted-foreground">…</td>}
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-0.5">
                                <button
                                  className="p-1.5 rounded hover:bg-muted"
                                  title="Просмотр"
                                  onClick={() => { setEditingDoc(doc); setEditJson(JSON.stringify(doc, null, 2)); }}
                                >
                                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                                </button>
                                <button
                                  className="p-1.5 rounded hover:bg-muted"
                                  title="Редактировать"
                                  onClick={() => { setEditingDoc(doc); setEditJson(JSON.stringify(doc, null, 2)); }}
                                >
                                  <Edit className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                </button>
                                <button
                                  className="p-1.5 rounded hover:bg-destructive/10"
                                  title="Удалить"
                                  onClick={() => setConfirmDeleteDoc(doc)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </button>
                                <button
                                  className="p-1.5 rounded hover:bg-muted"
                                  title="Копировать JSON"
                                  onClick={() => { navigator.clipboard.writeText(JSON.stringify(doc, null, 2)); toast.success('Скопировано'); }}
                                >
                                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Delete document confirm */}
                {confirmDeleteDoc && (
                  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDeleteDoc(null)}>
                    <Card className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                      <CardHeader>
                        <CardTitle className="text-base text-destructive flex items-center gap-2"><AlertCircle className="h-5 w-5" /> Удалить документ?</CardTitle>
                        <CardDescription>ID: {getDocId(confirmDeleteDoc)}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteDoc(null)}>Отмена</Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDeleteDoc(confirmDeleteDoc)}>Удалить</Button>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <div className="text-xs text-muted-foreground">
                      {docPage * docLimit + 1}–{Math.min((docPage + 1) * docLimit, docTotal)} из {docTotal}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={docPage === 0} onClick={() => setDocPage(p => p - 1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs px-2">{docPage + 1} / {totalPages}</span>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={docPage >= totalPages - 1} onClick={() => setDocPage(p => p + 1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Database className="h-10 w-10 mb-3 opacity-20" />
              <p className="text-sm">Выберите коллекцию</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}


function ClearButton({ label, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (err) {
      toast.error('Ошибка при удалении');
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={handleConfirm} disabled={loading}>
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Да, удалить'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirming(false)}>
          Нет
        </Button>
      </div>
    );
  }

  return (
    <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => setConfirming(true)}>
      <Trash2 className="h-3 w-3 mr-1" /> {label}
    </Button>
  );
}

// ==================== SUBSCRIPTION TAB ====================

const PERIOD_LABELS = { 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' };
const PERIOD_OPTIONS = [
  { months: 1, price: 360, discount: 0, total: 360 },
  { months: 3, price: 340, discount: 6, total: 1020 },
  { months: 6, price: 310, discount: 14, total: 1860 },
  { months: 12, price: 260, discount: 28, total: 3120 },
];

function SubscriptionTab({ userId, userName, userEmail }) {
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(1);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminSubscriptionAPI.get(userId);
      setSubData(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки подписки');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await adminSubscriptionAPI.activate(userId, { period_months: selectedPeriod });
      toast.success(`Подписка «Про» на ${PERIOD_LABELS[selectedPeriod]} активирована`);
      setConfirmCancel(false);
      loadSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка активации подписки');
    } finally {
      setActivating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await adminSubscriptionAPI.cancel(userId);
      toast.success('Подписка отменена');
      setConfirmCancel(false);
      loadSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отмены подписки');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <LoadingState />;

  const current = subData?.current;
  const history = subData?.history || [];
  const isActive = current?.status === 'active';

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  const selectedPricing = PERIOD_OPTIONS.find(p => p.months === selectedPeriod);

  return (
    <div className="p-5 space-y-5">
      {/* Current Plan */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <Crown className="h-4 w-4" /> Текущий тариф
        </h3>
        
        {isActive ? (
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-emerald-500" />
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">Тариф «Про»</span>
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                  Активен
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Период</p>
                  <p className="font-medium">{PERIOD_LABELS[current.period_months] || `${current.period_months} мес.`}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Стоимость</p>
                  <p className="font-medium">{current.price_per_month} ₽/мес ({current.total_price} ₽)</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Начало</p>
                  <p className="font-medium">{formatDate(current.started_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Истекает</p>
                  <p className="font-medium">{formatDate(current.expires_at)}</p>
                </div>
              </div>
              {current.activated_by === 'admin' && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Активировано администратором
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4 flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <div>
                <span className="font-semibold text-amber-600 dark:text-amber-400">Демо-версия</span>
                <p className="text-xs text-muted-foreground">Лимит: 3 заказа, 3 задачи, 3 клиента</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Separator />

      {/* Activate / Extend Subscription */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4" /> {isActive ? 'Продлить подписку' : 'Активировать подписку'}
        </h3>
        
        <div className="space-y-3">
          {/* Period Selector */}
          <div className="grid grid-cols-2 gap-2">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.months}
                onClick={() => setSelectedPeriod(opt.months)}
                className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                  selectedPeriod === opt.months
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
              >
                <div className="text-sm font-semibold">{PERIOD_LABELS[opt.months]}</div>
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{opt.price} ₽<span className="text-xs font-normal text-muted-foreground">/мес</span></div>
                <div className="text-xs text-muted-foreground">Итого: {opt.total} ₽</div>
                {opt.discount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] px-1.5">
                    -{opt.discount}%
                  </Badge>
                )}
              </button>
            ))}
          </div>

          <Button
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-10"
            onClick={handleActivate}
            disabled={activating}
          >
            {activating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Crown className="h-4 w-4 mr-2" />
            )}
            {isActive ? 'Продлить' : 'Активировать'} «Про» — {selectedPricing?.total} ₽ за {PERIOD_LABELS[selectedPeriod]}
          </Button>
        </div>
      </div>

      {/* Cancel Subscription */}
      {isActive && (
        <>
          <Separator />
          <div>
            <h3 className="text-sm font-semibold text-destructive flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4" /> Отмена подписки
            </h3>
            {!confirmCancel ? (
              <Button variant="destructive" size="sm" onClick={() => setConfirmCancel(true)} className="w-full">
                Отменить подписку
              </Button>
            ) : (
              <div className="space-y-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                <p className="text-sm text-destructive">Подписка будет немедленно отменена. Пользователь вернётся на демо-версию.</p>
                <div className="flex gap-2">
                  <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelling} className="flex-1">
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Да, отменить'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmCancel(false)} className="flex-1">
                    Назад
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Subscription History */}
      {history.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
              <History className="h-4 w-4" /> История подписок
            </h3>
            <div className="space-y-2">
              {history.map((h, i) => {
                const statusLabel = { active: 'Активна', expired: 'Истекла', cancelled: 'Отменена', extended: 'Продлена' };
                const statusColor = { active: 'text-emerald-600 bg-emerald-500/10', expired: 'text-muted-foreground bg-muted', cancelled: 'text-red-500 bg-red-500/10', extended: 'text-blue-500 bg-blue-500/10' };
                return (
                  <div key={h.id || i} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-muted/30 text-sm">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Про — {PERIOD_LABELS[h.period_months] || `${h.period_months} мес.`}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColor[h.status] || ''}`}>
                          {statusLabel[h.status] || h.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(h.started_at)} — {formatDate(h.expires_at)}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{h.total_price} ₽</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function StatMini({ icon: Icon, label, value }) {
  return (
    <div className="bg-muted/50 rounded-lg p-3 text-center">
      <Icon className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
      <div className="text-sm font-bold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

// ==================== SERVER MAINTENANCE TAB ====================

function ServerTab() {
  const [serverStatus, setServerStatus] = useState(null);
  const [services, setServices] = useState([]);
  const [uploads, setUploads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [restarting, setRestarting] = useState({});
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleaningTestUsers, setCleaningTestUsers] = useState(false);
  const [showFiles, setShowFiles] = useState(false);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const results = await Promise.allSettled([
        api.get('/admin/server/status'),
        api.get('/admin/server/services'),
        api.get('/admin/server/uploads'),
      ]);
      if (results[0].status === 'fulfilled') setServerStatus(results[0].value.data);
      if (results[1].status === 'fulfilled') setServices(results[1].value.data.services || []);
      if (results[2].status === 'fulfilled') setUploads(results[2].value.data);
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) toast.error('Ошибка загрузки данных сервера');
    } catch (e) {
      toast.error('Ошибка загрузки данных сервера');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRestart = async (service) => {
    if (!window.confirm(`Перезапустить ${service}?`)) return;
    setRestarting(prev => ({...prev, [service]: true}));
    try {
      const res = await api.post('/admin/server/restart', { service });
      toast.success(res.data.message);
      setTimeout(() => fetchAll(true), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка перезапуска');
    } finally {
      setRestarting(prev => ({...prev, [service]: false}));
    }
  };

  const handleCleanup = async () => {
    if (!uploads || uploads.orphan_count === 0) {
      toast.info('Нет файлов для очистки');
      return;
    }
    if (!window.confirm(`Удалить ${uploads.orphan_count} неиспользуемых файлов?`)) return;
    setCleaningUp(true);
    try {
      const res = await api.delete('/admin/server/uploads/cleanup');
      toast.success(`Удалено ${res.data.deleted} файлов, освобождено ${formatBytes(res.data.freed)}`);
      fetchAll(true);
    } catch (e) {
      toast.error('Ошибка очистки');
    } finally {
      setCleaningUp(false);
    }
  };

  const handleCleanupTestUsers = async () => {
    if (!window.confirm('Удалить всех пользователей с email @test.ru и все их данные? Это необратимо!')) return;
    setCleaningTestUsers(true);
    try {
      const res = await api.delete('/admin/users/cleanup/test-users');
      if (res.data.deleted_users === 0) {
        toast.info('Тестовые пользователи не найдены');
      } else {
        toast.success(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка очистки');
    } finally {
      setCleaningTestUsers(false);
    }
  };

  if (loading) return <LoadingState />;

  const s = serverStatus;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Обслуживание сервера</h3>
          <p className="text-sm text-muted-foreground">Мониторинг, файлы и сервисы</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchAll(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {/* Server monitoring cards */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${s.cpu.percent > 80 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                  <Cpu className={`h-5 w-5 ${s.cpu.percent > 80 ? 'text-red-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">CPU</p>
                  <p className="text-xl font-bold">{s.cpu.percent}%</p>
                  <p className="text-[10px] text-muted-foreground">{s.cpu.cores} ядер</p>
                </div>
              </div>
              <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${s.cpu.percent > 80 ? 'bg-red-500' : s.cpu.percent > 50 ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{width: `${s.cpu.percent}%`}} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${s.memory.percent > 80 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`}>
                  <MemoryStick className={`h-5 w-5 ${s.memory.percent > 80 ? 'text-red-600' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Память</p>
                  <p className="text-xl font-bold">{s.memory.percent}%</p>
                  <p className="text-[10px] text-muted-foreground">{formatBytes(s.memory.used)} / {formatBytes(s.memory.total)}</p>
                </div>
              </div>
              <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${s.memory.percent > 80 ? 'bg-red-500' : s.memory.percent > 50 ? 'bg-yellow-500' : 'bg-purple-500'}`} style={{width: `${s.memory.percent}%`}} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${s.disk.percent > 85 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                  <HardDrive className={`h-5 w-5 ${s.disk.percent > 85 ? 'text-red-600' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Диск</p>
                  <p className="text-xl font-bold">{s.disk.percent}%</p>
                  <p className="text-[10px] text-muted-foreground">{formatBytes(s.disk.used)} / {formatBytes(s.disk.total)}</p>
                </div>
              </div>
              <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${s.disk.percent > 85 ? 'bg-red-500' : s.disk.percent > 60 ? 'bg-yellow-500' : 'bg-emerald-500'}`} style={{width: `${s.disk.percent}%`}} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-orange-100 dark:bg-orange-900/30">
                  <Activity className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Аптайм</p>
                  <p className="text-xl font-bold">{s.uptime.display}</p>
                  <p className="text-[10px] text-muted-foreground">{s.platform.system} • Python {s.platform.python}{s.platform.env === 'docker' ? ' • Docker' : ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Services management */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-4 w-4" />
              Сервисы
            </CardTitle>
            <CardDescription>Управление процессами сервера</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {services.map(svc => (
                <div key={svc.name} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${svc.status === 'RUNNING' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                    <div>
                      <p className="font-medium text-sm">{svc.name}</p>
                      <p className="text-xs text-muted-foreground">{svc.status} {svc.info}</p>
                    </div>
                  </div>
                  {(svc.name === 'backend' || svc.name === 'frontend') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestart(svc.name)}
                      disabled={restarting[svc.name]}
                    >
                      {restarting[svc.name] ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      <span className="ml-1.5">Перезапуск</span>
                    </Button>
                  )}
                </div>
              ))}
              {services.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Нет данных о сервисах</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* File management */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Файлы загрузок
            </CardTitle>
            <CardDescription>Управление загруженными файлами</CardDescription>
          </CardHeader>
          <CardContent>
            {uploads && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold">{uploads.files.length}</p>
                    <p className="text-[10px] text-muted-foreground">Всего файлов</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold">{formatBytes(uploads.total_size)}</p>
                    <p className="text-[10px] text-muted-foreground">Общий размер</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className={`text-lg font-bold ${uploads.orphan_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {uploads.orphan_count}
                    </p>
                    <p className="text-[10px] text-muted-foreground">Неиспользуемых</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowFiles(!showFiles)}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    {showFiles ? 'Скрыть файлы' : 'Показать файлы'}
                  </Button>
                  <Button
                    variant={uploads.orphan_count > 0 ? "destructive" : "outline"}
                    size="sm"
                    className="flex-1"
                    onClick={handleCleanup}
                    disabled={cleaningUp || uploads.orphan_count === 0}
                  >
                    {cleaningUp ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Очистить ({uploads.orphan_count})
                  </Button>
                </div>

                {showFiles && uploads.files.length > 0 && (
                  <ScrollArea className="h-[360px] rounded-lg border">
                    <div className="p-2 space-y-1.5">
                      {uploads.files.map((f, i) => (
                        <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-xs ${f.is_orphan ? 'bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800' : 'bg-muted/30'}`}>
                          {/* Thumbnail for images */}
                          {f.is_image ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`}
                                alt={f.name}
                                className="w-10 h-10 rounded-md object-cover border bg-muted cursor-pointer hover:opacity-80 transition-opacity"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </a>
                          ) : (
                            <div className="w-10 h-10 rounded-md border bg-muted/50 flex items-center justify-center flex-shrink-0">
                              <Paperclip className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          {/* File info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate font-mono text-xs">{f.name}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-muted-foreground">{formatBytes(f.size)}</span>
                              {f.owner && (
                                <span className="text-muted-foreground truncate">• {f.owner}</span>
                              )}
                            </div>
                          </div>
                          {/* Status badges */}
                          <div className="flex-shrink-0">
                            {f.is_orphan ? (
                              <Badge variant="outline" className="text-[9px] bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400">
                                не используется
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400">
                                привязан
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                {showFiles && uploads.files.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Нет загруженных файлов</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Users Cleanup */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Тестовые пользователи
            </CardTitle>
            <CardDescription>Удаление пользователей с email @test.ru и всех их данных</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCleanupTestUsers}
              disabled={cleaningTestUsers}
            >
              {cleaningTestUsers ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {cleaningTestUsers ? 'Удаление...' : 'Удалить тестовых пользователей'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 Б';
  const k = 1024;
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function formatMoney(n) {
  if (n === undefined || n === null) return '0 ₽';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);
}
